import * as React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Tabs } from "@jhool-io/fe-components";
import HarmonizedView from "../../../../shared-ui/HarmonizedView/HarmonizedView";
import { cn } from "../../../../utils/helpers";
import SingleNoteActions from "../SingleNoteActions/SingleNoteActions";
import { useFetchSessionNote } from "../../../../hooks/queries/note";
import { useFetchRemitClaims } from "../../../remits/hooks/remits.queries";
import { useDoesUserHavePermission } from "../../../../hooks/permissions";
import { UserPermisions, UserRole } from "../../../../utils/types/user";
import Loader from "../../../../shared-ui/Loader/Loader";
import { useAppSelector } from "../../../../hooks/useRedux";
import NoteNavigation from "../NoteNavigation/NoteNavigation";
import { useNotesListFromLocalStorage } from "../../hooks/useNotesListFromLocalStorage";
import { AiSessionNoteReviewBoundary } from "../../../../context/AiSessionNoteReviewContext";

const NoteItemsWrapper = React.lazy(
    () => import("../NoteItemsWrapper/NoteItemsWrapper")
);

const ClaimsWrapper = React.lazy(
    () => import("../../../claims/components/ClaimsWrapper/ClaimsWrapper")
);

const RemitsWrapper = React.lazy(
    () => import("../../../remits/components/RemitsWrapper/RemitsWrapper")
);

const PaymentTabWrapper = React.lazy(
    () =>
        import(
            "../../../billing/components/PaymentTabWrapper/PaymentTabWrapper"
        )
);

function SingleNoteViewContent() {
    const [searchParams, setSearchParams] = useSearchParams();

    const params = useParams();
    const { clientId, noteId } = params;

    const tabInUrl = searchParams.get("tab") || "note";

    const { practice } = useAppSelector((state) => state.userPractice);

    const { data } = useFetchSessionNote(
        clientId as string,
        noteId as string,
        Boolean(clientId) && Boolean(noteId)
    );

    const { noteListType } = useNotesListFromLocalStorage();

    const activeBtnInLS = localStorage.getItem("activeBtn");

    const viewFrom = searchParams.get("viewFrom") || "";

    const hideNavigationButtons =
        viewFrom !== "session_note" ||
        activeBtnInLS === "my_supervisees_notes" ||
        noteListType === "flagged_notes";

    const handleSetTabInUrl = (tab: string) => {
        searchParams.set("tab", tab);
        setSearchParams(searchParams);
    };

    const remitsData = useFetchRemitClaims({
        note_id: noteId,
    });

    const canViewClaimsTab =
        Boolean(data?.data.is_eligible_for_claim_creation) ||
        Boolean(data?.data.claim_id);

    const canSeePaymentTab = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_INVOICE_READ,
    ]);

    const isOnlyProvider =
        practice?.roles?.length === 1 &&
        practice?.roles?.includes(UserRole.PROVIDER);

    const tabItems = [
        {
            label: "Session Note",
            key: "note",
            children: (
                <div>
                    <React.Suspense fallback={<Loader />}>
                        <NoteItemsWrapper />
                    </React.Suspense>
                </div>
            ),
            onClick: () => handleSetTabInUrl("note"),
        },
        {
            label: "Claims",
            key: "claims",
            children: (
                <div>
                    <React.Suspense fallback={<Loader />}>
                        <ClaimsWrapper allowUserGenerate={canViewClaimsTab} />
                    </React.Suspense>
                </div>
            ),
            onClick: () => handleSetTabInUrl("claims"),
            disabled: !canViewClaimsTab,
        },
        {
            label: "Payments History",
            key: "payments",
            children: (
                <div>
                    <React.Suspense fallback={<Loader />}>
                        <PaymentTabWrapper />
                    </React.Suspense>
                </div>
            ),
            disabled:
                !canSeePaymentTab ||
                (data?.data.is_archived && !data?.data.invoice_id),
        },
        {
            key: "remit",
            label: "Remits",
            children: (
                <div>
                    <React.Suspense fallback={<Loader />}>
                        <RemitsWrapper />
                    </React.Suspense>
                </div>
            ),
            onClick: () => handleSetTabInUrl("remit"),
            disabled:
                !remitsData?.data?.data || remitsData?.data?.data?.length === 0,
        },
    ];

    return (
        <>
            <div className="mb-2">
                <NoteNavigation
                    currentNoteId={noteId as string}
                    basePath="notes"
                    viewFrom={viewFrom}
                    hideNavigation={hideNavigationButtons}
                />
            </div>
            <div>
                <HarmonizedView topRightAction={<SingleNoteActions />} />
            </div>
            <div className="mt-[--spacing-header]">
                {practice?.roles?.includes(UserRole.AUDITOR_ADMIN) ||
                isOnlyProvider ? (
                    <div>
                        <React.Suspense fallback={<Loader />}>
                            <NoteItemsWrapper />
                        </React.Suspense>
                    </div>
                ) : (
                    <Tabs
                        key={tabInUrl}
                        items={tabItems}
                        className={cn("!w-[100%] !items-end")}
                        defaultActiveKey={tabInUrl}
                    />
                )}
            </div>
        </>
    );
}

export default function SingleNoteView() {
    const { noteId } = useParams();
    return (
        <AiSessionNoteReviewBoundary noteId={noteId as string}>
            <SingleNoteViewContent />
        </AiSessionNoteReviewBoundary>
    );
}
